import supabaseClient from "../lib/supabaseClient";
import { User } from "@supabase/supabase-js";

export interface UserProfile {
  id: string;
  full_name?: string;
  email: string;
  role?: string;
  customer_id?: string;
  created_at: string;
  updated_at: string;
  onboarding?: {
    role?: string;
    companyName?: string;
    companyWebsite?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    selectedContacts?: any[];
    metrics?: string[];
    engineeringMetrics?: string[];
    integrations?: string[];
    step?: number;
    completed_steps?: string[];
    onboarding_complete?: boolean;
  };
}

export interface UserSettings {
  id: string;
  user_id: string;
  timezone: string;
  preferred_language: string;
  email_notifications: boolean;
  kpi_visibility: any;
  created_at: string;
  updated_at: string;
}

export interface Company {
  id: string;
  name: string;
  description?: string;
  website_url?: string;
  user_id: string;
  created_at: string;
  updated_at: string;
}

export interface Integration {
  id: string;
  user_id: string;
  provider: string;
  integration_status: string;
  created_at: string;
  updated_at: string;
}

export interface UserRole {
  id: string;
  user_id: string;
  type: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface FullUserProfile {
  profile: UserProfile;
  settings?: UserSettings;
  company?: Company;
  integrations: Integration[];
  role?: UserRole;
}

export const userService = {
  /**
   * Get comprehensive user profile data
   */
  async getFullProfile(): Promise<FullUserProfile | null> {
    try {
      const {
        data: { user },
        error: authError,
      } = await supabaseClient.auth.getUser();

      if (authError || !user) {
        throw new Error("User not authenticated");
      }

      // Get user profile - use maybeSingle() to handle missing records
      const { data: profile, error: profileError } = await supabaseClient
        .from("users")
        .select("*")
        .eq("id", user.id)
        .maybeSingle();

      // Log any errors from the profile query
      if (profileError) {
        console.error("Profile query error:", profileError);
      }

      // If no profile exists in database, create one from auth user data
      let userProfile: UserProfile;
      
      if (!profile) {
        console.log("No profile found in database, using auth user data");
        console.log("User ID being queried:", user.id);
        userProfile = {
          id: user.id,
          email: user.email || "",
          full_name: user.user_metadata?.full_name || user.user_metadata?.name || "",
          created_at: user.created_at || new Date().toISOString(),
          updated_at: new Date().toISOString(),
          onboarding: {},
        };
      } else {
        userProfile = profile;
      }

      // Get user settings (optional - don't fail if missing)
      const { data: settings } = await supabaseClient
        .from("user_settings")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();

      // Get company info (optional - don't fail if missing)
      const { data: company } = await supabaseClient
        .from("companies")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();

      // Get integrations (optional - don't fail if missing)
      const { data: integrations } = await supabaseClient
        .from("integrations")
        .select(
          "id, user_id, provider, integration_status, created_at, updated_at"
        )
        .eq("user_id", user.id);

      // Get user role (optional - don't fail if missing)
      const { data: role } = await supabaseClient
        .from("roles")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();

      return {
        profile: userProfile,
        settings: settings || undefined,
        company: company || undefined,
        integrations: integrations || [],
        role: role || undefined,
      };
    } catch (error) {
      console.error("getFullProfile error:", error);
      throw error;
    }
  },

  /**
   * Update user profile information
   */
  async updateProfile(
    updates: Partial<Pick<UserProfile, "full_name" | "email">>
  ): Promise<void> {
    const {
      data: { user },
      error: authError,
    } = await supabaseClient.auth.getUser();

    if (authError || !user) {
      throw new Error("User not authenticated");
    }

    const { error } = await supabaseClient
      .from("users")
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq("id", user.id);

    if (error) {
      throw error;
    }
  },

  /**
   * Update user settings
   */
  async updateSettings(
    updates: Partial<
      Pick<
        UserSettings,
        | "timezone"
        | "preferred_language"
        | "email_notifications"
        | "kpi_visibility"
      >
    >
  ): Promise<void> {
    const {
      data: { user },
      error: authError,
    } = await supabaseClient.auth.getUser();

    if (authError || !user) {
      throw new Error("User not authenticated");
    }

    const { error } = await supabaseClient
      .from("user_settings")
      .upsert({
        user_id: user.id,
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq("user_id", user.id);

    if (error) {
      throw error;
    }
  },

  /**
   * Update company information
   */
  async updateCompany(
    updates: Partial<Pick<Company, "name" | "description" | "website_url">>
  ): Promise<void> {
    const {
      data: { user },
      error: authError,
    } = await supabaseClient.auth.getUser();

    if (authError || !user) {
      throw new Error("User not authenticated");
    }

    console.log("updateCompany called with:", { updates, userId: user.id });

    // First check if company exists
    const { data: existingCompany, error: selectError } = await supabaseClient
      .from("companies")
      .select("id, name, description, website_url")
      .eq("user_id", user.id)
      .maybeSingle();

    console.log("Existing company:", existingCompany, "Error:", selectError);

    if (existingCompany) {
      // Update existing company
      console.log("Updating existing company with ID:", existingCompany.id);
      const { data: updatedData, error } = await supabaseClient
        .from("companies")
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq("user_id", user.id)
        .select();

      console.log("Update result:", { updatedData, error });

      if (error) {
        console.error("Company update error:", error);
        throw new Error(`Failed to update company: ${error.message}`);
      }
    } else {
      // Insert new company
      console.log("Creating new company for user:", user.id);
      const { data: insertedData, error } = await supabaseClient
        .from("companies")
        .insert({
          user_id: user.id,
          ...updates,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .select();

      console.log("Insert result:", { insertedData, error });

      if (error) {
        console.error("Company insert error:", error);
        throw new Error(`Failed to create company: ${error.message}`);
      }
    }

    console.log("Company update completed successfully");
  },

};
